import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { getOutboundTraceHeaders, isTracerInitialized, debugLog } from '@kempoweroyj/ce-lib-rest-tracer';
import { ConfigService } from './config.service';
import { requestUrlTargetsConfiguredApi, shouldAttachTraceHeadersToBackendRequests } from './browser-trace-policy';

const LOG_PREFIX = '[TracePropagationInterceptor]';

/**
 * HTTP interceptor that propagates W3C Trace Context headers to outbound requests.
 *
 * This enables distributed tracing across frontend and backend services by
 * adding `traceparent` and optionally `tracestate` headers to HTTP requests.
 *
 * Span creation is handled by Sentry's browserTracingIntegration (configured in
 * sentry/init.ts) which automatically instruments fetch/XHR — this interceptor
 * only needs to inject the trace context headers.
 *
 * @see https://www.w3.org/TR/trace-context/
 */
@Injectable()
export class TracePropagationInterceptor implements HttpInterceptor {
    constructor(private readonly configService: ConfigService) {}

    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        const augmented = this.tryCloneWithTraceHeaders(req);
        return next.handle(augmented ?? req);
    }

    /**
     * Returns a cloned request with trace headers, or null if headers must not be attached.
     */
    private tryCloneWithTraceHeaders(req: HttpRequest<unknown>): HttpRequest<unknown> | null {
        const apiBaseUrl = this.configService.config.apiBaseUrl;

        if (!shouldAttachTraceHeadersToBackendRequests(apiBaseUrl)) {
            debugLog(LOG_PREFIX, 'Skipping trace headers (SPA host differs from API host)', {
                url: req.url,
                browserHost: globalThis.window?.location?.hostname ?? '',
                apiBaseUrl,
            });
            return null;
        }

        if (!requestUrlTargetsConfiguredApi(req.url, apiBaseUrl)) {
            debugLog(LOG_PREFIX, 'Skipping trace headers (request does not target configured API)', {
                url: req.url,
                apiBaseUrl,
            });
            return null;
        }

        if (!isTracerInitialized()) {
            debugLog(LOG_PREFIX, 'Tracer not initialized, skipping trace headers', { url: req.url });
            return null;
        }

        const traceHeaders = getOutboundTraceHeaders();
        if (!traceHeaders || Object.keys(traceHeaders).length === 0) {
            debugLog(LOG_PREFIX, 'No trace headers available', { url: req.url });
            return null;
        }

        debugLog(LOG_PREFIX, 'Injecting trace headers', {
            url: req.url,
            traceparent: traceHeaders['traceparent']?.substring(0, 50),
            headerKeys: Object.keys(traceHeaders),
        });

        return req.clone({
            setHeaders: traceHeaders as Record<string, string>,
        });
    }
}
